/**
 * Processing-time estimate helpers (Part IX). The server (`GET /api/eta`) returns a
 * rolling median of recent upload→checked durations; these helpers phrase it as an
 * ESTIMATE (never a promise), and switch to explicit early-estimate wording while
 * there is not enough history for the median to mean anything.
 */

export interface EtaInfo {
  medianMs: number | null;
  sampleSize: number;
  basis: 'history' | 'default';
  defaultMs: number;
}

/** "about 12 minutes" / "about an hour and 10 minutes" - deliberately coarse. */
export function formatApproxDuration(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return `about ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hourPart = hours === 1 ? 'an hour' : `${hours} hours`;
  return rest > 0 ? `about ${hourPart} and ${rest} minutes` : `about ${hourPart}`;
}

/** Valuer-side sentence shown after upload and on "My uploads". */
export function valuerEtaSentence(eta: EtaInfo): string {
  if (eta.basis === 'history' && eta.medianMs !== null) {
    return `Usually ready for review in ${formatApproxDuration(eta.medianMs)}.`;
  }
  return `Early estimate: usually ready for review within ${formatApproxDuration(eta.defaultMs)} — we haven't processed enough reports yet for a firmer number.`;
}

/**
 * Reviewer-side live remainder for a queued/checking report: the median minus the
 * time already elapsed since submission. Returns null when there's no submission
 * timestamp to count from.
 */
export function remainingEtaMs(eta: EtaInfo, submittedAt: string | null, now: number): number | null {
  if (!submittedAt) return null;
  const expected = eta.medianMs ?? eta.defaultMs;
  return expected - (now - new Date(submittedAt).getTime());
}

/** "Estimated ready in about 8 minutes" / "should be ready any moment now". */
export function reviewerEtaSentence(eta: EtaInfo, submittedAt: string | null, now: number): string {
  const remaining = remainingEtaMs(eta, submittedAt, now);
  const early = eta.basis !== 'history' ? ' (early estimate — limited history so far)' : '';
  if (remaining === null) {
    const expected = eta.medianMs ?? eta.defaultMs;
    return `Usually takes ${formatApproxDuration(expected)}${early}.`;
  }
  if (remaining <= 60_000) return `Should be ready any moment now${early}.`;
  return `Estimated ready in ${formatApproxDuration(remaining)}${early}.`;
}
